import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip, TextField,
    MenuItem, Tabs, Tab, InputAdornment, LinearProgress, IconButton
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon,
    CheckCircle as CheckIcon, Cancel as CancelIcon,
    Warning as WarningIcon, Verified as VerifiedIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

const DataVerification = () => {
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [classFilter, setClassFilter] = useState('');

    const students = [
        { id: 1, admNo: 'ADM001', name: 'Aarav Patil', class: 'Class 10A', personalInfo: true, parentInfo: true, addressInfo: true, documents: true, fees: true, aadhaar: true, photo: true, tc: false },
        { id: 2, admNo: 'ADM002', name: 'Sneha Sharma', class: 'Class 10A', personalInfo: true, parentInfo: true, addressInfo: false, documents: true, fees: true, aadhaar: true, photo: true, tc: true },
        { id: 3, admNo: 'ADM003', name: 'Rohan Kulkarni', class: 'Class 9B', personalInfo: true, parentInfo: false, addressInfo: true, documents: false, fees: false, aadhaar: true, photo: false, tc: false },
        { id: 4, admNo: 'ADM004', name: 'Priya Desai', class: 'Class 8A', personalInfo: true, parentInfo: true, addressInfo: true, documents: true, fees: true, aadhaar: true, photo: true, tc: true },
        { id: 5, admNo: 'ADM005', name: 'Amit Singh', class: 'Class 7A', personalInfo: true, parentInfo: true, addressInfo: true, documents: false, fees: true, aadhaar: false, photo: true, tc: false },
        { id: 6, admNo: 'ADM006', name: 'Diya Wagh', class: 'Class 5B', personalInfo: true, parentInfo: true, addressInfo: true, documents: true, fees: false, aadhaar: false, photo: false, tc: false },
    ];

    const fields = ['personalInfo', 'parentInfo', 'addressInfo', 'documents', 'fees', 'aadhaar', 'photo', 'tc'];
    const fieldLabels = { personalInfo: 'Personal', parentInfo: 'Parent', addressInfo: 'Address', documents: 'Docs', fees: 'Fees', aadhaar: 'Aadhaar', photo: 'Photo', tc: 'TC' };

    const getCompleteness = (s) => {
        const filled = fields.filter(f => s[f]).length;
        return Math.round((filled / fields.length) * 100);
    };

    const isComplete = (s) => getCompleteness(s) === 100;

    const filteredStudents = students.filter(s => {
        const matchTab = tab === 0 ? true : tab === 1 ? isComplete(s) : !isComplete(s);
        const matchClass = !classFilter || s.class === classFilter;
        const matchSearch = !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.admNo.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTab && matchClass && matchSearch;
    });

    const completeCount = students.filter(isComplete).length;
    const incompleteCount = students.length - completeCount;
    const avgCompleteness = Math.round(students.reduce((a, s) => a + getCompleteness(s), 0) / students.length);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Data Verification</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} size="small">Refresh</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total Students</Typography>
                        <Typography variant="h4" fontWeight={700}>{students.length}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <VerifiedIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Complete</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{completeCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Incomplete</Typography>
                        <Typography variant="h4" fontWeight={700} color="warning.main">{incompleteCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Avg Completeness</Typography>
                        <Typography variant="h4" fontWeight={700}>{avgCompleteness}%</Typography>
                        <LinearProgress variant="determinate" value={avgCompleteness}
                            sx={{ mt: 0.5, height: 5, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: avgCompleteness >= 80 ? '#4caf50' : '#ff9800' } }} />
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${students.length})`} />
                    <Tab label={`Complete (${completeCount})`} />
                    <Tab label={`Incomplete (${incompleteCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" placeholder="Search by name or admission number..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth select size="small" label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                                <MenuItem value="">All Classes</MenuItem>
                                {[...new Set(students.map(s => s.class))].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Adm No.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                    {fields.map(f => <TableCell key={f} align="center" sx={{ fontWeight: 700, fontSize: 11 }}>{fieldLabels[f]}</TableCell>)}
                                    <TableCell sx={{ fontWeight: 700 }}>Completeness</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredStudents.map(s => {
                                    const pct = getCompleteness(s);
                                    return (
                                        <TableRow key={s.id} hover>
                                            <TableCell sx={{ fontWeight: 600 }}>{s.admNo}</TableCell>
                                            <TableCell>{s.name}</TableCell>
                                            <TableCell>{s.class}</TableCell>
                                            {fields.map(f => (
                                                <TableCell key={f} align="center">
                                                    {s[f] ? <CheckIcon sx={{ color: '#4caf50', fontSize: 18 }} /> : <CancelIcon sx={{ color: '#e53935', fontSize: 18 }} />}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ minWidth: 120 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LinearProgress variant="determinate" value={pct}
                                                        sx={{
                                                            flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: pct === 100 ? '#4caf50' : pct >= 70 ? '#ff9800' : '#e53935' }
                                                        }} />
                                                    <Typography variant="caption" sx={{ minWidth: 30 }}>{pct}%</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={pct === 100 ? 'Complete' : 'Incomplete'}
                                                    color={pct === 100 ? 'success' : 'warning'} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>
        </Box>
    );
};

export default DataVerification;
